/**
 * ADSP Ads Performance dashboard page.
 * Keeps ADSP's reporting contract in a thin configuration over PlatformDashboard.
 */
import React from 'react';
import { useTranslation } from 'src/arb-wrapper';
import type { PaidMediaInsightsConfig } from 'src/components/paid-media-insights/InsightsSection';
import PlatformDashboard from 'src/components/paid-media-dashboard/PlatformDashboard';
import { formatPercent } from 'src/utils/dashboardUtils';

const ADSP_TABS = [
  { value: 'campaign', label: 'Campaign' },
  { value: 'ad', label: 'Ad' },
];

const ADSP_COLUMN_WIDTHS: Record<string, number> = {
  campaign_name: 600,
  ad_name: 600,
  advertiser_name: 600,
};

const ADSP_FORMAT_OVERRIDES = {
  ctr: formatPercent,
};

// Video funnel metrics are intentionally excluded from Summary; they appear under All Metrics only.
const ADSP_SUMMARY_COLUMNS = [
  'campaign_name',
  'ad_name',
  'advertiser_name',
  'currency',
  'spend',
  'supply_cost',
  'impressions',
  'clicks',
  'ctr',
  'cpc',
  'cpm',
];

const ADSP_ALWAYS_VISIBLE_KEYS = ['campaign_name', 'ad_name'];

const ADSP_CURRENCY_COLUMN_KEY = 'currency';

const ADSP_INSIGHTS_CONFIG = {
  cardMetrics: [
    {
      key: 'spend',
      label: 'paid_media_insights_metric_spend',
      numberFormat: {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: 'currency',
      },
    },
    {
      key: 'impressions',
      label: 'paid_media_insights_metric_impressions',
      numberFormat: { maximumFractionDigits: 0, style: 'decimal' },
    },
    {
      key: 'clicks',
      label: 'paid_media_insights_metric_clicks',
      numberFormat: { maximumFractionDigits: 0, style: 'decimal' },
    },
    {
      key: 'ctr',
      label: 'paid_media_insights_metric_ctr',
      numberFormat: { maximumFractionDigits: 2, style: 'percent' },
    },
  ],
  engagementMetrics: [
    { key: 'video_impressions', label: 'paid_media_insights_metric_video_impressions' },
    { key: 'video_starts', label: 'paid_media_insights_metric_video_starts' },
    { key: 'first_quartile', label: 'paid_media_insights_metric_first_quartile' },
    { key: 'midpoint', label: 'paid_media_insights_metric_midpoint' },
    { key: 'third_quartile', label: 'paid_media_insights_metric_third_quartile' },
    { key: 'complete', label: 'paid_media_insights_metric_completes' },
    { key: 'video_clicks', label: 'paid_media_insights_metric_video_clicks' },
  ],
} satisfies PaidMediaInsightsConfig;

const AdspDashboard: React.FC = () => {
  const { t } = useTranslation();

  const columnTooltips: Record<string, string> = {
    campaign_name: t('paid_media_tooltip_campaign_name'),
    ad_name: t('paid_media_tooltip_ad_name'),
    advertiser_name: t('paid_media_tooltip_tiktok_advertiser_name'),
    currency: t('paid_media_tooltip_tiktok_currency'),
    spend: t('paid_media_tooltip_spend'),
    impressions: t('paid_media_tooltip_impressions'),
    clicks: t('paid_media_tooltip_clicks'),
    ctr: t('paid_media_tooltip_ctr'),
    cpc: t('paid_media_tooltip_cpc'),
    cpm: t('paid_media_tooltip_cpm'),
    video_impressions: t('paid_media_tooltip_impressions'),
    video_starts: t('paid_media_tooltip_tiktok_video_play_actions'),
    first_quartile: t('paid_media_tooltip_tiktok_video_views_p25'),
    midpoint: t('paid_media_tooltip_tiktok_video_views_p50'),
    third_quartile: t('paid_media_tooltip_tiktok_video_views_p75'),
    complete: t('paid_media_tooltip_tiktok_video_views_p100'),
    video_clicks: t('paid_media_tooltip_clicks'),
  };

  return (
    <PlatformDashboard
      alwaysVisibleKeys={ADSP_ALWAYS_VISIBLE_KEYS}
      columnTooltips={columnTooltips}
      columnWidths={ADSP_COLUMN_WIDTHS}
      currencyColumnKey={ADSP_CURRENCY_COLUMN_KEY}
      formatOverrides={ADSP_FORMAT_OVERRIDES}
      insightsConfig={ADSP_INSIGHTS_CONFIG}
      summaryColumns={ADSP_SUMMARY_COLUMNS}
      tabs={ADSP_TABS}
      title="ADSP Ads Performance"
      view="adsp"
    />
  );
};

export default AdspDashboard;
